import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useLayers } from '@ais-ui/react-openlayers';
import { useSettings } from '../../Settings/SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { DropdownMenuItem, useToast } from '@ais-ui/ui-core';
import { isShapeLabelShown } from '../isShapeShownOnStartUp';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface ShowLabelDropdownButtonProps {
  shape: GeoJson;
}

export const ShowLabelDropdownButton = ({
  shape,
}: ShowLabelDropdownButtonProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { messages } = useLocalization();

  const { getLayerById, addLabels, removeLabels } = useLayers();

  const { settings, dataUpdatedAt } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();
  const shapeId = shape?.properties?.id ? shape.properties.id : 'tempShapeId';

  const isLabelShown = useMemo(() => {
    return isShapeLabelShown(settings, shapeId);
  }, [settings, shapeId, dataUpdatedAt]);

  const onShowLabel = () => {
    setIsSubmitting(true);
    const layer = getLayerById(shapeId);
    if (layer) {
      addLabels(layer);
    }
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        shape_labels: [
          ...(settings['ribbonPlugin.settings']?.shape_labels || []),
          shapeId,
        ],
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-show-label-shapes-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsShapesShowLabelSuccessUpdateMessage,
        });
        setIsSubmitting(false);
      },
      onError: () => {
        toast({
          className: 'update-show-label-shapes-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsShapesShowLabelFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  const onHideLabel = () => {
    setIsSubmitting(true);
    const layer = getLayerById(shapeId);
    if (layer) {
      removeLabels(layer);
    }
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        shape_labels:
          settings['ribbonPlugin.settings']?.shape_labels?.filter(
            (id: string) => id !== shapeId
          ) || [],
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-show-label-shapes-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsShapesShowLabelSuccessHideMessage,
        });
        setIsSubmitting(false);
      },
      onError: () => {
        toast({
          className: 'update-show-label-shapes-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsShapesShowLabelFailedHideMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return (
    <DropdownMenuItem
      onClick={isLabelShown ? onHideLabel : onShowLabel}
      disabled={isSubmitting}
    >
      {isLabelShown ? 'Hide labels' : 'Show labels'}
    </DropdownMenuItem>
  );
};
